import { useParams, useLocation, useHistory } from 'react-router-dom';
import { makeStyles } from '@material-ui/core';

import { useAdminGetOrganizationById } from '../../hooks/get-organization-by-id.hook';
import { Skeleton } from '@material-ui/lab';
import { Organization, type OrganizationType } from '../../types/organization.type';
import OrganizationTypeChip from '../../components/Chips/OrganizationTypeChip.component';
import React from 'react';
import OrganizationInformationsTab from '../../components/Containers/OrganizationInformationsTab.component';
import OrganizationMembersTab from '../../components/Containers/OrganizationMembersTab.component';
import OrganizationDeliveryPointsTab from '../../components/Containers/OrganizationDeliveryPointsTab.component';
import OrganizationOrdersTab from '../../components/Containers/OrganizationOrdersTab.component';
import { OrganizationQuotesTab } from '../../components/Containers/OrganizationQuotesTab.component';
import { OrganizationInvoicesTab } from '../../../invoices/components/Containers/OrganizationInvoicesTab.component';
import { useUpdateOrganization } from '../../hooks/update-organization.hook';
import TabsController from '../../../shared/components/Tabs/ButtonTabsController';

const useStyles = makeStyles(() => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    height: '86svh',
    gap: '1rem',
  },

  header: {
    display: 'flex',
    alignItems: 'center',
    gap: '1rem',

    '& > h2': {
      margin: 0,

      '& > span': {
        paddingLeft: 6,
        fontSize: 14,
        lineHeight: '12px',
        opacity: 0.7,
      },
    },
  },
}));

const OrganizationHeader = ({ label, type }: { label: string; type: OrganizationType }) => {
  const classes = useStyles();

  return (
    <div className={classes.header}>
      <h2>{label}</h2>
      <OrganizationTypeChip type={type} />
    </div>
  );
};

const StatisticsTab = () => {
  return <div>Statistics</div>;
};

export default function AdminOrganizationDetailsView() {
  const location = useLocation();
  const history = useHistory();
  const params = new URLSearchParams(location.search);
  const currentTab = params.get('tab') || 'informations';

  const classes = useStyles({});
  const { organizationId } = useParams();

  const { isLoading, organization, fetchOrganization } = useAdminGetOrganizationById({ organizationId });
  const { updateOrganization, isUpdating } = useUpdateOrganization();

  const handleTabChange = (tab: string) => {
    const newParams = new URLSearchParams(location.search);
    newParams.set('tab', tab);
    history.push({ search: newParams.toString() });
  };

  const handleOrganizationUpdated = async (editedFields: Partial<Organization>) => {
    if (isLoading || !organization) return;

    await updateOrganization({ organizationId: organization.id, organizationData: editedFields });
    await fetchOrganization();
  };

  const handleRefreshOrganization = async () => {
    await fetchOrganization();
  };

  return (
    <section className={classes.root}>
      {isLoading && <Skeleton variant="rect" animation="wave" />}
      {!isLoading && organization && (
        <React.Fragment>
          <OrganizationHeader label={organization.name} type={organization.type} />
          <TabsController
            size="small"
            tabs={[
              { id: 'statistics', label: 'Statistiques', value: 'statistics', disabled: true },
              { id: 'informations', label: 'Informations', value: 'informations' },
              { id: 'delivery-points', label: 'Points de livraison', value: 'delivery-points' },
              { id: 'membres', label: 'Membres', value: 'membres' },
              { id: 'orders', label: 'Commandes', value: 'orders' },
              { id: 'quotes', label: 'Devis', value: 'quotes' },
              { id: 'invoicing', label: 'Facturation', value: 'invoicing' },
            ]}
            currentTabId={currentTab}
            onTabChange={handleTabChange}
          />
          {currentTab === 'statistics' && <StatisticsTab />}
          {currentTab === 'informations' && (
            <OrganizationInformationsTab
              organization={organization}
              updateOrganization={handleOrganizationUpdated}
              isUpdating={isUpdating}
            />
          )}
          {currentTab === 'membres' && (
            <OrganizationMembersTab organization={organization} refreshOrganization={handleRefreshOrganization} />
          )}
          {currentTab === 'delivery-points' && (
            <OrganizationDeliveryPointsTab
              organization={organization}
              refreshOrganization={handleRefreshOrganization}
            />
          )}
          {currentTab === 'orders' && <OrganizationOrdersTab organization={organization} />}
          {currentTab === 'quotes' && (
            <OrganizationQuotesTab organization={organization} refreshOrganization={handleRefreshOrganization} />
          )}
          {currentTab === 'invoicing' && <OrganizationInvoicesTab organization={organization} />}
        </React.Fragment>
      )}
    </section>
  );
}
